// =============================================================================
// Derives the whole UI palette from SCHOOL.brand and writes it to CSS variables.
//
// Why derive rather than hand-pick: a brand colour is chosen to work on a sign,
// not behind body text. A mid-tone orange is the worked example —
//
//   orange on white          2.6:1   fails WCAG AA (needs 4.5:1)
//   white text on orange     2.5:1   fails
//   near-black on orange     7.9:1   passes comfortably
//
// So the topbar uses orange with the logo's near-black wordmark colour, and
// anything that must read as brand-coloured TEXT on white (links, active tabs)
// uses a darkened shade computed to clear the threshold. Every other brand gets
// the same treatment automatically — including a dark one, where the maths
// picks white text instead.
// Verify any change with: npm run brand:check
// =============================================================================

import { SCHOOL } from '../config/school.js';
import { bestTextOn, readableOn, tint, darken, contrast, INK } from './color.js';

export function buildTheme(brand = SCHOOL.brand.color, ink = SCHOOL.brand.ink) {
  return {
    '--brand': brand,
    // Readable text/icon colour ON the brand colour (topbar, primary buttons).
    '--on-brand': bestTextOn(brand) === '#ffffff' ? '#ffffff' : ink || INK,
    // Brand colour usable AS text on a light background (links, active tab).
    '--brand-text': readableOn(brand, '#ffffff', 4.5),
    // Pressed/hover surface.
    '--brand-dark': darken(brand, 0.15),
    // Soft brand wash for selected rows and highlights.
    '--brand-tint': tint(brand, 0.9),
    '--ink': ink || INK,
  };
}

export function applyTheme(target = document.documentElement) {
  const vars = buildTheme();
  for (const [k, v] of Object.entries(vars)) target.style.setProperty(k, v);

  // Keep the browser/OS chrome in step with the header.
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', vars['--brand']);

  return vars;
}

// Used by the theme check script. Kept here so the thresholds live with the
// code that has to satisfy them.
export function auditTheme(brand = SCHOOL.brand.color, ink = SCHOOL.brand.ink) {
  const t = buildTheme(brand, ink);
  return [
    { name: 'text on topbar / primary button', fg: t['--on-brand'], bg: t['--brand'], min: 4.5 },
    { name: 'brand text on white (links, tabs)', fg: t['--brand-text'], bg: '#ffffff', min: 4.5 },
    { name: 'body ink on page background', fg: t['--ink'], bg: '#f4f6f8', min: 4.5 },
    { name: 'ink on brand tint (highlight rows)', fg: t['--ink'], bg: t['--brand-tint'], min: 4.5 },
  ].map((c) => ({ ...c, ratio: +contrast(c.fg, c.bg).toFixed(2), pass: contrast(c.fg, c.bg) >= c.min }));
}
